/** 전표(출고/입고) 목록의 열 헤더 클릭 정렬 로직 (부작용 없음).
 *
 * OrderCrudTab 은 서버에서 받은 orders 를 이 함수로 정렬해 표시한다.
 * 순수 함수라 단위 테스트가 가능하다.
 */

export type ListSortKey =
  | 'order_date' | 'order_no' | 'partner' | 'count' | 'subtotal' | 'vat' | 'total'

export interface ListSort {
  key: ListSortKey
  dir: 'asc' | 'desc'
}

/** 정렬에 필요한 필드만. OrderRecord 가 이 형태를 만족한다. */
export interface SortableOrder {
  order_no: string
  order_date: string
  partner_name: string
  subtotal: string | number
  vat_total: string | number
  total: string | number
  lines?: readonly unknown[]
  lines_count?: number       // slim 목록(라인 미포함)의 건수
}

export function orderSortValue(o: SortableOrder, key: ListSortKey): string | number {
  switch (key) {
    case 'order_date': return o.order_date          // 'YYYY-MM-DD' → 사전순 = 날짜순
    case 'order_no': return o.order_no
    case 'partner': return o.partner_name || ''
    case 'count': return o.lines_count ?? o.lines?.length ?? 0
    case 'subtotal': return Number(o.subtotal)
    case 'vat': return Number(o.vat_total)
    case 'total': return Number(o.total)
  }
}

/** orders 를 sort 기준으로 정렬한 새 배열 반환. sort 가 없으면 원본 그대로.
 *  Array.prototype.sort 는 안정정렬이라 동률은 기존 순서를 유지한다. */
export function sortOrders<T extends SortableOrder>(orders: readonly T[], sort: ListSort | null): readonly T[] {
  if (!sort) return orders
  const { key, dir } = sort
  return [...orders].sort((a, b) => {
    const va = orderSortValue(a, key), vb = orderSortValue(b, key)
    const cmp = typeof va === 'number' && typeof vb === 'number'
      ? va - vb
      : String(va).localeCompare(String(vb), 'ko')
    return dir === 'asc' ? cmp : -cmp
  })
}

/** 헤더 클릭 시 다음 정렬 상태 — 같은 열 오름차순이면 내림차순, 그 외엔 오름차순부터. */
export function nextSort(cur: ListSort | null, key: ListSortKey): ListSort {
  return cur && cur.key === key && cur.dir === 'asc'
    ? { key, dir: 'desc' }
    : { key, dir: 'asc' }
}
